import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarMonthRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  LocationOnRounded,
  OpenInNewRounded,
} from '@mui/icons-material';
import type { FeaturedEvent } from '@dosfilos/domain';
import { formatDate } from '../../../lib/i18n-format';
import {
  eventPhase,
  safeExternalUrl,
  safeImageUrl,
  type EventPhase,
} from '../../../lib/featured-events';

// Tema «ambar» de events.dosfilos.com (public-site.css): la tarjeta lleva la
// estética del sitio al que manda, sobre el fondo del landing del seminario.
const AMBER = '#c97806';
const AMBER_STRONG = '#e3a64a';
const ON_AMBER = '#140c02';
const RAISED = '#121515';
const RULE = 'rgba(232,228,220,0.12)';
const TEXT = '#e8e4dc';
const TEXT_2 = '#a09890';
const TEXT_MUTED = '#7a756a';

const DWELL_MS = 7000;
const EXIT_MS = 420;
const ENTER_MS = 600;
const DRAG_THRESHOLD = 60;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => window.matchMedia?.(REDUCED_MOTION)?.matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(REDUCED_MOTION);
    if (!mq) return;
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

/** Misma regla que la página del evento en events: «Exponiendo» + <em>la Palabra</em>. */
function EventTitle({ title }: { title: string }) {
  const words = title.trim().split(/\s+/);
  if (words.length < 3 || title.length > 34) {
    return <h2 className={`fe-title${title.length > 26 ? ' is-long' : ''}`}>{title}</h2>;
  }
  return (
    <h2 className="fe-title">
      {words[0]} <em>{words.slice(1).join(' ')}</em>
    </h2>
  );
}

function day(ymd: string): number {
  return Number(ymd.slice(8, 10));
}

function monthShort(ymd: string): string {
  return formatDate(ymd, { month: 'short' }).replace('.', '');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function EventCard({
  event,
  index,
  total,
  pos,
  className,
  today,
}: {
  event: FeaturedEvent;
  index: number;
  total: number;
  pos: number | null; // 0 = adelante; null = fuera de la pila visible
  className: string;
  today: string;
}) {
  const { t } = useTranslation('sis');
  const front = pos === 0;
  const phase: EventPhase = eventPhase(event, today);
  const url = safeExternalUrl(event.registrationUrl);
  const image = safeImageUrl(event.imageUrl);
  const end = event.endDate || event.startDate;
  const sameDay = end === event.startDate;
  const sameMonth = end.slice(0, 7) === event.startDate.slice(0, 7);

  const big = sameDay
    ? String(day(event.startDate))
    : sameMonth
      ? `${day(event.startDate)}–${day(end)}`
      : `${day(event.startDate)}·${day(end)}`;
  const small = sameMonth
    ? `${monthShort(event.startDate)} ${event.startDate.slice(0, 4)}`
    : `${monthShort(event.startDate)}–${monthShort(end)}`;
  const longFmt: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
  const longDate = sameDay
    ? capitalize(formatDate(event.startDate, longFmt))
    : t('publicLanding.events.dateRange', {
        start: capitalize(formatDate(event.startDate, longFmt)),
        end: formatDate(end, longFmt),
      });

  const countdown =
    phase.kind === 'upcoming'
      ? t('publicLanding.events.countdown', { count: phase.days })
      : phase.kind === 'today'
        ? t('publicLanding.events.startsToday')
        : phase.kind === 'live'
          ? t('publicLanding.events.live')
          : null;
  const live = phase.kind === 'today' || phase.kind === 'live';

  return (
    <article
      className={`fe-card ${className}`}
      data-pos={pos === null ? 'hidden' : String(pos)}
      role="group"
      aria-roledescription={t('publicLanding.events.roleSlide')}
      aria-label={t('publicLanding.events.slideLabel', {
        index: index + 1,
        total,
        title: event.title,
      })}
      aria-hidden={!front}
    >
      {image && (
        // Franja decorativa: el texto de la tarjeta ya dice todo, por eso alt="".
        <div className="fe-media">
          <img src={image} alt="" loading="lazy" decoding="async" draggable={false} />
          <span className="fe-tag">{event.label}</span>
          {countdown && (
            <span className={`fe-countdown${live ? ' is-live' : ''}`}>{countdown}</span>
          )}
        </div>
      )}
      <div className="fe-inner">
        {!image && (
          <div className="fe-top">
            <span className="fe-tag">{event.label}</span>
            {countdown && (
              <span className={`fe-countdown${live ? ' is-live' : ''}`}>{countdown}</span>
            )}
          </div>
        )}
        <EventTitle title={event.title} />
        {event.description && <p className="fe-desc">{event.description}</p>}
        <div className="fe-when">
          <div className="fe-datebox">
            <b>{big}</b>
            <small>{small}</small>
          </div>
          <div className="fe-meta">
            <div>
              <CalendarMonthRounded />
              <span>{longDate}</span>
            </div>
            {event.venue && (
              <div>
                <LocationOnRounded />
                <span>{event.venue}</span>
              </div>
            )}
          </div>
        </div>
        <div className="fe-foot">
          {event.price && (
            <div className="fe-price">
              <small>{t('publicLanding.events.priceLabel')}</small>
              <b>{event.price}</b>
            </div>
          )}
          {url && (
            <a
              className="fe-btn"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              draggable={false}
              tabIndex={front ? 0 : -1}
            >
              {t('publicLanding.events.register')}
              <OpenInNewRounded />
              <span className="fe-sr">{t('publicLanding.events.newTab')}</span>
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * Eventos destacados del primer plano del landing. Con uno, una tarjeta; con
 * varios, una pila en carrusel: avanza sola cada 7 s (el avance ES el final de
 * la barra del punto activo, así que pausar la animación pausa el carrusel),
 * se detiene con el mouse encima, con foco o con la pestaña oculta, y no rota
 * sola si el sistema pide reducir el movimiento.
 */
export function FeaturedEvents({
  events,
  today,
  serif,
  sans,
}: {
  events: FeaturedEvent[];
  today: string;
  serif: string;
  sans: string;
}) {
  const { t } = useTranslation('sis');
  const reduceMotion = usePrefersReducedMotion();
  const total = events.length;
  const multi = total > 1;

  const [active, setActive] = useState(0);
  const [exiting, setExiting] = useState<number | null>(null);
  const [entering, setEntering] = useState<number | null>(null);
  const [snapping, setSnapping] = useState<number | null>(null);
  const [cycle, setCycle] = useState(0);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  const busy = useRef(false);
  const stackRef = useRef<HTMLDivElement>(null);
  const draggedRef = useRef(false);

  const paused = hover || focus || hidden || reduceMotion;

  useEffect(() => {
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Si cambia la lista (el landing recarga datos), volver al primero.
  useEffect(() => {
    setActive(0);
  }, [total]);

  // Entrada desde la izquierda: un cuadro con la tarjeta afuera y sin
  // transición, y en el siguiente se suelta hacia su lugar.
  useEffect(() => {
    if (entering === null) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntering(null));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [entering]);

  useEffect(() => {
    if (snapping === null) return;
    const raf = requestAnimationFrame(() => setSnapping(null));
    return () => cancelAnimationFrame(raf);
  }, [snapping]);

  const go = useCallback(
    (dir: 1 | -1, target?: number) => {
      if (!multi || busy.current) return;
      const next = target ?? (active + dir + total) % total;
      if (next === active) return;
      const forward = target === undefined ? dir > 0 : next > active;
      setCycle((c) => c + 1);
      if (reduceMotion) {
        setActive(next);
        return;
      }
      busy.current = true;
      if (forward) {
        // La de adelante sale hacia la izquierda y la siguiente avanza.
        const out = active;
        setExiting(out);
        setActive(next);
        window.setTimeout(() => {
          setExiting(null);
          setSnapping(out);
          busy.current = false;
        }, EXIT_MS);
      } else {
        setEntering(next);
        setActive(next);
        window.setTimeout(() => {
          busy.current = false;
        }, ENTER_MS);
      }
    },
    [active, multi, reduceMotion, total],
  );

  // ── Arrastre (móvil y mouse): solo la tarjeta de adelante ──
  const drag = useRef<{
    el: HTMLElement;
    x: number;
    y: number;
    dx: number;
    on: boolean;
    id: number;
  } | null>(null);
  const onPointerDown = (e: ReactPointerEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('.fe-card[data-pos="0"]');
    if (!multi || !el || busy.current) return;
    drag.current = { el, x: e.clientX, y: e.clientY, dx: 0, on: false, id: e.pointerId };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    d.dx = e.clientX - d.x;
    if (!d.on) {
      if (Math.abs(d.dx) < 8 || Math.abs(d.dx) < Math.abs(e.clientY - d.y)) return;
      d.on = true;
      d.el.classList.add('is-dragging');
      stackRef.current?.setPointerCapture(e.pointerId);
      setHover(true);
    }
    d.el.style.transform = `translateX(${d.dx}px) rotate(${d.dx / 28}deg)`;
  };
  const onPointerEnd = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    if (!d.on) return;
    d.el.classList.remove('is-dragging');
    d.el.style.transform = '';
    draggedRef.current = true;
    window.setTimeout(() => {
      draggedRef.current = false;
    }, 0);
    setHover(false);
    if (d.dx < -DRAG_THRESHOLD) go(1);
    else if (d.dx > DRAG_THRESHOLD) go(-1);
  };
  // Un arrastre que termina sobre el botón no debe abrir la inscripción.
  const onClickCapture = (e: ReactMouseEvent) => {
    if (draggedRef.current) e.preventDefault();
  };

  const posOf = (i: number): number | null => {
    const p = (i - active + total) % total;
    return p <= 2 ? p : null;
  };

  return (
    <aside
      className="fe-root"
      aria-roledescription={
        multi ? t('publicLanding.events.roleCarousel') : t('publicLanding.events.roleFeatured')
      }
      aria-label={
        multi ? t('publicLanding.events.headingMany') : t('publicLanding.events.headingOne')
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false);
      }}
      onKeyDown={(e) => {
        if (!multi) return;
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          go(1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          go(-1);
        }
      }}
    >
      <style>{css(serif, sans)}</style>
      <div className="fe-head">
        <span className="fe-heading">
          {multi ? t('publicLanding.events.headingMany') : t('publicLanding.events.headingOne')}
        </span>
        {multi && (
          <span className="fe-count">
            {t('publicLanding.events.counter', { current: active + 1, total })}
          </span>
        )}
      </div>

      <div className={`fe-wrap${paused ? ' is-paused' : ''}`}>
        <div
          ref={stackRef}
          className={`fe-stack${multi ? ' is-multi' : ''}`}
          aria-live={multi && !paused ? 'off' : 'polite'}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onClickCapture={onClickCapture}
        >
          {events.map((ev, i) => {
            const classes = [
              i === exiting ? 'is-out-left' : '',
              i === entering ? 'is-out-left no-anim' : '',
              i === snapping ? 'no-anim' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <EventCard
                key={ev.id}
                event={ev}
                index={i}
                total={total}
                pos={posOf(i)}
                className={classes}
                today={today}
              />
            );
          })}
        </div>

        {multi && (
          <div className="fe-controls">
            <div className="fe-dots" role="tablist" aria-label={t('publicLanding.events.pick')}>
              {events.map((ev, i) => (
                <button
                  key={ev.id}
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  aria-label={t('publicLanding.events.goTo', { index: i + 1, total })}
                  className={`fe-dot${i === active ? ' is-active' : ''}${reduceMotion ? ' is-static' : ''}`}
                  onClick={() => go(1, i)}
                >
                  {i === active && (
                    <span
                      key={cycle}
                      className="fe-fill"
                      onAnimationEnd={() => {
                        if (!paused) go(1);
                      }}
                    />
                  )}
                </button>
              ))}
            </div>
            <div className="fe-arrows">
              <button
                type="button"
                className="fe-arrow"
                aria-label={t('publicLanding.events.prev')}
                onClick={() => go(-1)}
              >
                <ChevronLeftRounded />
              </button>
              <button
                type="button"
                className="fe-arrow"
                aria-label={t('publicLanding.events.next')}
                onClick={() => go(1)}
              >
                <ChevronRightRounded />
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function css(serif: string, sans: string): string {
  return `
    .fe-root { position: relative; font-family: ${sans}; color: ${TEXT}; animation: fadeUp 1s 0.35s ease both; }
    .fe-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
    .fe-heading { font-size: 11px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: ${AMBER_STRONG}; }
    .fe-count { font-size: 11px; letter-spacing: 0.12em; color: ${TEXT_MUTED}; font-variant-numeric: tabular-nums; }

    .fe-stack { display: grid; position: relative; touch-action: pan-y; }
    .fe-stack.is-multi { padding: 22px 22px 0 0; }
    .fe-card {
      grid-area: 1 / 1; position: relative; display: flex; flex-direction: column;
      background:
        radial-gradient(120% 70% at 100% 0%, rgba(201,120,6,0.16) 0%, transparent 55%),
        linear-gradient(180deg, #151818 0%, ${RAISED} 100%);
      border: 1px solid ${RULE}; border-radius: 4px; padding: 28px 28px 24px;
      box-shadow: 0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04);
      transform-origin: 50% 100%;
      transition: transform 0.6s cubic-bezier(.2,.75,.2,1), opacity 0.6s ease, filter 0.6s ease;
      user-select: none;
    }
    .fe-card::before { content: ""; position: absolute; top: -1px; left: 28px; width: 56px; height: 2px; background: ${AMBER}; }
    .fe-card[data-pos="0"] { z-index: 3; transform: none; opacity: 1; }
    .fe-card[data-pos="1"] { z-index: 2; transform: translate(11px, -11px) scale(0.965); opacity: 0.55; filter: saturate(0.6); pointer-events: none; }
    .fe-card[data-pos="2"] { z-index: 1; transform: translate(22px, -22px) scale(0.93); opacity: 0.25; filter: saturate(0.4); pointer-events: none; }
    .fe-card[data-pos="hidden"] { z-index: 0; opacity: 0; transform: translate(22px, -22px) scale(0.93); pointer-events: none; }
    .fe-card[data-pos="1"] .fe-inner, .fe-card[data-pos="2"] .fe-inner, .fe-card[data-pos="hidden"] .fe-inner { visibility: hidden; }
    .fe-card.is-out-left { z-index: 4 !important; transform: translateX(-70%) rotate(-5deg) !important; opacity: 0 !important; }
    .fe-card.is-out-left .fe-inner { visibility: visible; }
    .fe-card.no-anim { transition: none !important; }
    .fe-card.is-dragging { transition: none; cursor: grabbing; }

    .fe-card::before { z-index: 2; }
    .fe-media {
      position: relative; margin: -28px -28px 20px; aspect-ratio: 2 / 1;
      background: #1a1612; border-radius: 3px 3px 0 0; overflow: hidden;
    }
    .fe-media img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
    .fe-media::after {
      content: ""; position: absolute; inset: 0; pointer-events: none;
      background: linear-gradient(180deg, rgba(13,15,15,0.25) 0%, rgba(13,15,15,0) 30%, rgba(20,23,23,0.55) 68%, #141717 100%);
    }
    .fe-media .fe-tag { position: absolute; left: 28px; bottom: 12px; z-index: 1; text-shadow: 0 1px 8px rgba(0,0,0,0.6); }
    .fe-media .fe-countdown {
      position: absolute; top: 14px; right: 14px; z-index: 1;
      background: rgba(13,15,15,0.6); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    }
    .fe-inner { flex: 1; display: flex; flex-direction: column; gap: 18px; }
    .fe-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .fe-tag { font-size: 10.5px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: ${AMBER_STRONG}; }
    .fe-countdown {
      display: inline-flex; align-items: center; gap: 7px; padding: 5px 10px; border-radius: 999px;
      border: 1px solid rgba(227,166,74,0.28); background: rgba(201,120,6,0.08);
      font-size: 11.5px; font-weight: 500; color: ${AMBER_STRONG}; white-space: nowrap; font-variant-numeric: tabular-nums;
    }
    .fe-countdown::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: ${AMBER_STRONG}; box-shadow: 0 0 0 3px rgba(227,166,74,0.18); }
    .fe-countdown.is-live::before { animation: fePulse 1.6s ease-in-out infinite; }
    @keyframes fePulse { 50% { box-shadow: 0 0 0 6px rgba(227,166,74,0); } }

    .fe-title { font-family: ${serif}; font-weight: 500; font-size: clamp(2rem, 2.6vw, 2.6rem); line-height: 1.02; margin: 0; text-wrap: balance; letter-spacing: -0.005em; color: ${TEXT}; }
    .fe-title em { font-style: italic; color: ${AMBER_STRONG}; font-weight: 500; }
    .fe-title.is-long { font-size: clamp(1.7rem, 2.1vw, 2.1rem); }
    .fe-desc { margin: -6px 0 0; font-size: 14px; line-height: 1.6; color: ${TEXT_2}; }

    .fe-when { display: grid; grid-template-columns: auto 1fr; gap: 0 18px; align-items: center; padding: 16px 0; border-top: 1px solid ${RULE}; border-bottom: 1px solid ${RULE}; }
    .fe-datebox { display: flex; flex-direction: column; align-items: center; min-width: 64px; padding-right: 18px; border-right: 1px solid ${RULE}; }
    .fe-datebox b { font-family: ${serif}; font-weight: 500; font-size: 30px; line-height: 1; letter-spacing: -0.01em; font-variant-numeric: tabular-nums lining-nums; }
    .fe-datebox small { margin-top: 5px; font-size: 10px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: ${AMBER_STRONG}; white-space: nowrap; }
    .fe-meta { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
    .fe-meta div { display: flex; align-items: flex-start; gap: 8px; font-size: 13.5px; line-height: 1.4; color: ${TEXT}; }
    .fe-meta div + div { color: ${TEXT_2}; }
    .fe-meta svg { font-size: 15px; flex: none; margin-top: 2px; color: ${TEXT_MUTED}; }

    .fe-foot { margin-top: auto; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
    .fe-price { display: flex; flex-direction: column; }
    .fe-price small { font-size: 10px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: ${TEXT_MUTED}; }
    .fe-price b { font-family: ${serif}; font-weight: 500; font-size: 24px; line-height: 1.15; font-variant-numeric: lining-nums; }
    .fe-btn {
      position: relative; display: inline-flex; align-items: center; gap: 10px; margin-left: auto;
      background: ${AMBER}; color: ${ON_AMBER}; text-decoration: none;
      padding: 13px 22px; border-radius: 2px; font-size: 12.5px; font-weight: 600;
      letter-spacing: 0.08em; text-transform: uppercase; white-space: nowrap;
      box-shadow: 0 8px 24px rgba(201,120,6,0.25);
      transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
    }
    .fe-btn:hover { filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 10px 28px rgba(201,120,6,0.35); }
    .fe-btn svg { font-size: 15px; }
    .fe-btn:focus-visible, .fe-arrow:focus-visible, .fe-dot:focus-visible { outline: 2px solid ${AMBER_STRONG}; outline-offset: 3px; }
    .fe-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

    .fe-controls { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 16px; }
    .fe-dots { display: flex; gap: 8px; align-items: center; }
    .fe-dot { position: relative; width: 22px; height: 3px; padding: 0; border: 0; border-radius: 2px; background: rgba(232,228,220,0.16); cursor: pointer; overflow: visible; transition: width 0.35s ease; }
    .fe-dot::after { content: ""; position: absolute; inset: -10px -2px; }
    .fe-dot.is-active { width: 44px; }
    .fe-fill { position: absolute; inset: 0; border-radius: 2px; transform-origin: left; transform: scaleX(0); background: ${AMBER_STRONG}; animation: feProgress ${DWELL_MS}ms linear forwards; }
    .fe-dot.is-static .fe-fill { animation: none; transform: scaleX(1); }
    .fe-wrap.is-paused .fe-fill { animation-play-state: paused; }
    @keyframes feProgress { to { transform: scaleX(1); } }
    .fe-arrows { display: flex; gap: 8px; }
    .fe-arrow { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; background: transparent; border: 1px solid ${RULE}; color: ${TEXT}; cursor: pointer; transition: border-color 0.2s ease, background 0.2s ease; }
    .fe-arrow:hover { border-color: rgba(227,166,74,0.5); background: rgba(201,120,6,0.08); }
    .fe-arrow svg { font-size: 20px; }

    @media (max-width: 639px) {
      .fe-card { padding: 24px 20px 20px; }
      .fe-card::before { left: 20px; }
      .fe-media { margin: -24px -20px 18px; }
      .fe-media .fe-tag { left: 20px; }
      .fe-stack.is-multi { padding: 16px 16px 0 0; }
      .fe-card[data-pos="1"] { transform: translate(8px, -8px) scale(0.97); }
      .fe-card[data-pos="2"], .fe-card[data-pos="hidden"] { transform: translate(16px, -16px) scale(0.94); }
      .fe-title { font-size: 2rem; }
      .fe-title.is-long { font-size: 1.7rem; }
      .fe-btn { width: 100%; justify-content: center; margin-left: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fe-root { animation: none; }
      .fe-card { transition: opacity 0.2s ease; }
      .fe-card.is-out-left { transform: none !important; }
      .fe-countdown.is-live::before { animation: none; }
    }
  `;
}
